import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Card, Badge, Spinner, EmptyState } from "@/components/ui";
import Input from "@/components/ui/Input";
import { getShadow } from "@/components/ui/ShadowBadge";
import { getLevel } from "@/components/ui/XPBar";
import clsx from "clsx";

const SHADOW_TIERS = ["alle", "sprössling", "flammenträger", "blitzdenker", "diamantkopf", "legende"];

export default function StudentList() {
  const navigate = useNavigate();
  const [search, setSearch]     = useState("");
  const [tierFilter, setTier]   = useState("alle");
  const [ageFilter, setAge]     = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["students", tierFilter, ageFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (tierFilter !== "alle") params.set("shadowTier", tierFilter);
      if (ageFilter)            params.set("age", ageFilter);
      return api.get(`/students?${params}`).then((r) => r.data);
    },
    refetchInterval: 30_000,
  });

  const students = (data?.students || []).filter((s) =>
    search.trim()
      ? s.user?.name?.toLowerCase().includes(search.toLowerCase())
      : true
  );

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="text-xl font-black">👥 Schüler</h1>
        <p className="text-xs text-gray-400 mt-0.5">{data?.students?.length || 0} registriert</p>
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <Input
          value={search}
          onChange={setSearch}
          placeholder="🔍 Name suchen..."
          icon="🔍"
        />
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {SHADOW_TIERS.map((t) => {
            const shadow = getShadow(
              t === "sprössling" ? 0 : t === "flammenträger" ? 100 :
              t === "blitzdenker" ? 400 : t === "diamantkopf" ? 1000 :
              t === "legende" ? 2500 : -1
            );
            return (
              <button
                key={t}
                onClick={() => setTier(t)}
                className={clsx(
                  "flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all",
                  t === tierFilter
                    ? "bg-violet-600 border-violet-500 text-white"
                    : "bg-nexus-s2 border-nexus-border text-gray-400"
                )}
              >
                {t === "alle" ? "🌟 Alle" : `${shadow.emoji} ${shadow.name}`}
              </button>
            );
          })}
        </div>
        <select
          value={ageFilter}
          onChange={(e) => setAge(e.target.value)}
          className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
        >
          <option value="">Alle Altersgruppen</option>
          {[8,9,10,11,12,13,14,15].map((a) => (
            <option key={a} value={a}>{a} Jahre</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center pt-10"><Spinner size="lg" /></div>
      ) : students.length === 0 ? (
        <EmptyState icon="👥" title="Keine Schüler" desc="Noch keine Schüler registriert." />
      ) : (
        <div className="space-y-2">
          {students.map((s) => {
            const xp     = s.xp || 0;
            const level  = getLevel(xp);
            const shadow = getShadow(xp);
            return (
              <Card
                key={s.id}
                hover
                onClick={() => navigate(`/students/${s.id}`)}
                className="p-3.5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-nexus-s2 border border-nexus-border flex items-center justify-center text-xl flex-shrink-0">
                    {s.user?.avatar || "🦊"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm truncate">{s.user?.name}</p>
                      <span className="text-xs">{shadow.emoji}</span>
                    </div>
                    <p className="text-[10px] text-gray-500">Lv.{level} · {xp.toLocaleString("de-DE")} XP · 🪙 {s.coins?.toLocaleString("de-DE")}</p>
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    <Badge color="purple">Lv.{level}</Badge>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
